/**
 * FreezeSnapshotPanel — Phase-2 step 1 (Full audit only). Shows a pre-freeze
 * summary across all 6 Phase-1 categories (reconciled / flagged / missing-proof)
 * so the owner sees exactly what they're locking in, then FREEZES the closing
 * snapshot: sets the cutoff instant and unlocks the physical count. HARD-gated
 * server-side (Phase-1 must be 6/6). PIN-authorized via AuthDialog.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../../contexts/AuthContext';
import { Button } from '../../ui/button';
import {
  Snowflake, AlertTriangle, CheckCircle2, Loader2, ImageOff, Flag, Lock, NotebookPen, Keyboard,
} from 'lucide-react';
import { toast } from 'sonner';
import AuthDialog from '../../AuthDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FreezeSnapshotPanel({ session, onFrozen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [freezing, setFreezing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/freeze-summary`);
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [session.id]);

  useEffect(() => { load(); }, [load]);

  const doFreeze = async () => {
    setFreezing(true);
    try {
      const res = await api.post(`${BACKEND_URL}/api/audit-wizard/sessions/${session.id}/freeze`, {});
      toast.success('Snapshot frozen — physical count unlocked');
      onFrozen?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not freeze');
    }
    setFreezing(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>;
  }
  if (!data) {
    return <p className="text-sm text-slate-400 py-8 text-center">Could not load freeze summary.</p>;
  }

  const frozen = data.frozen;
  const t = data.totals || {};

  return (
    <div className="space-y-4" data-testid="freeze-panel">
      {/* Frozen confirmation */}
      {frozen ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="freeze-frozen-banner">
          <Snowflake size={18} className="text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">Snapshot frozen</p>
            <p className="text-xs text-emerald-700 mt-0.5">Cutoff {String(data.cutoff_at || '').slice(0, 19).replace('T', ' ')}. Expected inventory, cash &amp; credit are locked as of this instant. The physical count is now unlocked.</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">No selling during the count</p>
            <p className="text-xs text-amber-700 mt-0.5">Freezing locks the expected balances at this instant. Close the cashier shift first, then freeze — do not make sales until the count is signed off.</p>
          </div>
        </div>
      )}

      {/* Pre-freeze summary */}
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Pre-freeze summary</span>
          <span className="text-xs text-slate-400">{t.done}/{t.total} done · {t.flagged} flagged · {t.missing_proof} missing proof</span>
        </div>
        <div className="divide-y divide-slate-100">
          {(data.rows || []).map(r => (
            <div key={r.category} className="flex items-center gap-3 px-4 py-2.5" data-testid={`freeze-row-${r.category}`}>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold border ${
                r.style === 'notebook' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-[#1A4D2E]/10 text-[#1A4D2E] border-[#1A4D2E]/20'}`}>
                {r.style === 'notebook' ? <NotebookPen size={9} /> : <Keyboard size={9} />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-700 truncate">{r.label}</p>
                <p className="text-[11px] text-slate-400">{r.done}/{r.total} {r.style === 'notebook' ? 'reconciled' : 'verified'}</p>
              </div>
              <div className="flex items-center gap-2.5 text-[11px] shrink-0">
                {r.flagged > 0 && <span className="text-red-600 flex items-center gap-1"><Flag size={11} />{r.flagged}</span>}
                {r.missing_proof > 0 && <span className="text-amber-600 flex items-center gap-1"><ImageOff size={11} />{r.missing_proof}</span>}
                {r.step_complete
                  ? <CheckCircle2 size={15} className="text-emerald-600" />
                  : <Lock size={13} className="text-slate-300" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {(t.flagged > 0 || t.missing_proof > 0) && !frozen && (
        <p className="text-xs text-slate-500">
          {t.flagged > 0 && <span><b className="text-red-600">{t.flagged} flagged</b> item(s) </span>}
          {t.flagged > 0 && t.missing_proof > 0 && 'and '}
          {t.missing_proof > 0 && <span><b className="text-amber-600">{t.missing_proof} missing proof</b> </span>}
          will carry into Review &amp; Findings — you can still freeze and resolve them at sign-off.
        </p>
      )}

      {/* Action */}
      {!frozen && (
        <div className="flex justify-end pt-2 border-t border-slate-100">
          <Button onClick={() => setAuthOpen(true)} disabled={freezing || !data.phase1_complete}
            data-testid="freeze-confirm-btn"
            className="bg-red-600 hover:bg-red-700 text-white h-10 px-6">
            {freezing ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Snowflake size={15} className="mr-1.5" />}
            Close Shift &amp; Freeze Snapshot
          </Button>
        </div>
      )}

      <AuthDialog
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        mode="either"
        context="audit_freeze_snapshot"
        title="Authorize Freeze"
        description="Freezing locks expected balances and starts the no-selling count window."
        onVerified={() => { setAuthOpen(false); doFreeze(); }}
      />
    </div>
  );
}
